import { useEffect, useRef } from "react";

/**
 * Toggles `data-revealed="true"` on the element when it enters the viewport.
 * Pair with the `.reveal`, `.reveal-up`, `.reveal-fade`, or `.reveal-stagger`
 * utility classes defined in src/index.css.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(options?: {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      el.setAttribute("data-revealed", "true");
      return;
    }

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      el.setAttribute("data-revealed", "true");
      return;
    }

    const once = options?.once ?? true;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).setAttribute("data-revealed", "true");
            if (once) io.unobserve(entry.target);
          } else if (!once) {
            (entry.target as HTMLElement).setAttribute("data-revealed", "false");
          }
        }
      },
      {
        threshold: options?.threshold ?? 0.15,
        rootMargin: options?.rootMargin ?? "0px 0px -10% 0px",
      }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [options?.threshold, options?.rootMargin, options?.once]);

  return ref;
}